import type { SelectProps } from '@radix-ui/react-select'
import * as React from 'react'

import {
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Skeleton,
} from '@/components/ui'
import { cn, slugify } from '@/utils'

interface SelectOption {
  value: string
  label: string
}

export interface SelectFieldProps extends SelectProps {
  id?: string
  value: SelectOption['value']
  options: SelectOption[]
  className?: string
  label: string
  error?: string | null
  helpText?: string
  placeholder?: string
  isLoading?: boolean
  isOptional?: boolean
}

const SelectField = React.forwardRef<HTMLInputElement, SelectFieldProps>(
  (
    {
      id,
      label,
      options,
      className,
      placeholder,
      error,
      helpText,
      isLoading,
      onValueChange,
      ...props
    },
    ref,
  ) => {
    const _id = id ? id : `${slugify(label)}_${crypto.randomUUID()}`
    return (
      <div className={cn('space-y-2', className)}>
        <Label htmlFor={_id} className="text-sm font-medium">
          {label}
        </Label>
        {isLoading ? (
          <Skeleton className="h-10 w-full" />
        ) : (
          <>
            <div>
              <Select
                {...props}
                onValueChange={(val) =>
                  onValueChange?.(val === 'none' ? '' : val)
                }
                required={props.required}
              >
                <SelectTrigger id={_id} disabled={isLoading} className="h-10">
                  <SelectValue placeholder={placeholder} />
                </SelectTrigger>
                <SelectContent>
                  {!props.required && (
                    <SelectItem value="none">None</SelectItem>
                  )}
                  {options.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {error && (
              <p className="text-xs font-semibold text-red-500">{error}</p>
            )}
            {helpText && (
              <p className="text-xs text-muted-foreground leading-relaxed">
                {helpText}
              </p>
            )}
          </>
        )}
      </div>
    )
  },
)
SelectField.displayName = 'SelectField'

export { SelectField }
